/**
 * iOS Safari 键盘适配 Hook
 *
 * 解决问题：
 * 1. 键盘弹起时，position: fixed 元素被遮挡
 * 2. 页面底部被键盘抬起
 * 3. 输入框被键盘遮挡
 * 4. iOS Standalone Mode (添加到主屏幕) 的点击错位问题
 *
 * 使用方法：
 * ```tsx
 * import { useKeyboardAdjustment } from '@/hooks/useKeyboardAdjustment';
 *
 * function MyComponent() {
 *   const { keyboardHeight, isKeyboardVisible } = useKeyboardAdjustment();
 *
 *   return (
 *     <div
 *       className="footer"
 *       style={{ bottom: keyboardHeight }}
 *     >
 *       Footer Content
 *     </div>
 *   );
 * }
 * ```
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { isIOSStandaloneMode } from '@/utils/platform';

interface KeyboardState {
  /** 键盘高度（px） */
  keyboardHeight: number;
  /** 键盘是否可见 */
  isKeyboardVisible: boolean;
  /** Visual Viewport 高度 */
  viewportHeight: number;
}

/**
 * 检测浏览器类型
 */
function detectBrowser() {
  const ua = navigator.userAgent;
  const isChrome = /Chrome/.test(ua) && !/Edge|Edg/.test(ua);
  const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua);
  return { isChrome, isSafari };
}

/**
 * 监听键盘弹起/收起，返回键盘高度和可见状态
 */
export function useKeyboardAdjustment(): KeyboardState {
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(
    window.visualViewport?.height || window.innerHeight
  );

  // 记录初始窗口高度，用于 Chrome 修正
  const initialWindowHeightRef = useRef(window.innerHeight);
  const { isChrome } = detectBrowser();

  const handleViewportResize = useCallback(() => {
    if (!window.visualViewport) {
      return;
    }

    const visualViewport = window.visualViewport;
    const currentViewportHeight = visualViewport.height;
    const windowHeight = window.innerHeight;
    const isStandalone = isIOSStandaloneMode();

    // 计算键盘高度 = window.innerHeight - visualViewport.height
    let calculatedKeyboardHeight = Math.max(0, windowHeight - currentViewportHeight);

    // Chrome 浏览器修正：Chrome 在某些情况下会多计算地址栏高度
    // 策略1: 如果检测到异常的键盘高度（超过初始屏幕高度的 45%），进行修正
    if (isChrome && calculatedKeyboardHeight > initialWindowHeightRef.current * 0.45) {
      // 使用更保守的计算方式
      // 典型的 iPad 横屏键盘高度约为 264-329px，不应超过 400px
      const maxReasonableKeyboardHeight = Math.min(400, initialWindowHeightRef.current * 0.4);
      calculatedKeyboardHeight = Math.min(calculatedKeyboardHeight, maxReasonableKeyboardHeight);
    }

    // 策略2: 如果键盘高度小于 100px，认为键盘已收起
    if (calculatedKeyboardHeight < 100) {
      calculatedKeyboardHeight = 0;
    }

    // 调试日志（开发环境）
    if (process.env.NODE_ENV === 'development') {
      console.log('[Keyboard] 浏览器:', isChrome ? 'Chrome' : 'Safari/Other');
      console.log('[Keyboard] window.innerHeight:', windowHeight);
      console.log('[Keyboard] visualViewport.height:', currentViewportHeight);
      console.log('[Keyboard] 计算的键盘高度:', calculatedKeyboardHeight);
    }

    setViewportHeight(currentViewportHeight);
    setKeyboardHeight(calculatedKeyboardHeight);
    setIsKeyboardVisible(calculatedKeyboardHeight > 150); // 阈值：150px

    // 更新 CSS 变量，供全局使用
    document.documentElement.style.setProperty(
      '--keyboard-height',
      `${calculatedKeyboardHeight}px`
    );
    document.documentElement.style.setProperty(
      '--viewport-height',
      `${currentViewportHeight}px`
    );

    // 添加/移除键盘可见的 class
    if (calculatedKeyboardHeight > 150) {
      document.documentElement.classList.add('keyboard-visible');
      document.body.classList.add('keyboard-visible');

      // iOS Standalone Mode 特殊处理
      if (isStandalone) {
        document.documentElement.classList.add('keyboard-visible-standalone');
        document.body.classList.add('keyboard-visible-standalone');
      }
    } else {
      document.documentElement.classList.remove('keyboard-visible');
      document.body.classList.remove('keyboard-visible');
      document.documentElement.classList.remove('keyboard-visible-standalone');
      document.body.classList.remove('keyboard-visible-standalone');

      // 键盘收起时,确保清理所有样式
      if (isStandalone) {
        // Standalone Mode: 重置所有可能被修改的样式
        document.body.style.position = '';
        document.body.style.height = '';
        document.body.style.overflow = '';
        const root = document.getElementById('root');
        if (root) {
          root.style.height = '';
          root.style.position = '';
          root.style.overflow = '';
        }
      }
    }

    // 强制 reflow (iOS Hack) - 确保样式变化立即生效
    // 读取 offsetHeight 会触发浏览器重新计算布局
    void document.body.offsetHeight;

    // 如果是 Standalone Mode,也强制 #root reflow
    if (isStandalone) {
      const root = document.getElementById('root');
      if (root) {
        void root.offsetHeight;
      }
    }

    // 修复点击偏差：调整 body 的定位
    if (calculatedKeyboardHeight > 150) {
      if (isStandalone) {
        // Standalone Mode: 完全不使用 transform,让页面自然滚动
        // CSS 会通过 .keyboard-visible-standalone 禁用 body 的 fixed 定位
        // 这样可以避免所有的点击位置问题和空白区域

        // 自动滚动输入框到可视区域
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
          setTimeout(() => {
            const rect = activeElement.getBoundingClientRect();
            const elementTop = rect.top + window.pageYOffset;
            const elementBottom = rect.bottom + window.pageYOffset;
            const viewportBottom = window.pageYOffset + currentViewportHeight;

            // 如果输入框被键盘遮挡,滚动到合适位置
            if (elementBottom > viewportBottom) {
              const scrollTarget = elementTop - 100; // 留出 100px 缓冲
              window.scrollTo({
                top: Math.max(0, scrollTarget),
                behavior: 'smooth'
              });
            }
          }, 100);
        }
      } else {
        // 普通浏览器模式: 使用 transform 补偿偏移
        const offsetTop = visualViewport.offsetTop || 0;

        if (offsetTop > 0) {
          document.body.style.transform = `translateY(-${offsetTop}px)`;
          document.body.style.transition = 'transform 0.3s ease';
        } else {
          document.body.style.transform = '';
        }
      }
    } else {
      // 键盘收起时
      if (!isStandalone) {
        // 普通浏览器模式: 清理 transform
        document.body.style.transform = '';
        document.body.style.transition = '';
      }
      // Standalone Mode: 不需要特殊处理,让页面保持当前滚动位置
    }
  }, [isChrome]);

  useEffect(() => {
    const visualViewport = window.visualViewport;

    if (!visualViewport) {
      console.warn('Visual Viewport API not supported');
      return;
    }

    // 初始化 CSS 变量
    document.documentElement.style.setProperty('--keyboard-height', '0px');
    document.documentElement.style.setProperty(
      '--viewport-height',
      `${visualViewport.height}px`
    );

    // 监听 viewport resize（包含键盘弹起/收起）
    visualViewport.addEventListener('resize', handleViewportResize);
    visualViewport.addEventListener('scroll', handleViewportResize);

    // Cleanup
    return () => {
      visualViewport.removeEventListener('resize', handleViewportResize);
      visualViewport.removeEventListener('scroll', handleViewportResize);
      document.documentElement.classList.remove('keyboard-visible');
      document.body.classList.remove('keyboard-visible');
      document.documentElement.classList.remove('keyboard-visible-standalone');
      document.body.classList.remove('keyboard-visible-standalone');
      document.body.style.transform = '';
      document.body.style.transition = '';
    };
  }, [handleViewportResize]);

  return {
    keyboardHeight,
    isKeyboardVisible,
    viewportHeight,
  };
}

/**
 * 输入框聚焦时，自动滚动到可视区域
 *
 * 使用方法：
 * ```tsx
 * import { useInputScrollIntoView } from '@/hooks/useKeyboardAdjustment';
 *
 * function MyForm() {
 *   const inputRef = useRef<HTMLInputElement>(null);
 *   useInputScrollIntoView(inputRef);
 *
 *   return <Input ref={inputRef} />;
 * }
 * ```
 */
export function useInputScrollIntoView(
  inputRef: React.RefObject<HTMLElement>
): void {
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const handleFocus = () => {
      // 延迟执行，等待键盘动画完成
      setTimeout(() => {
        if (!window.visualViewport) {
          input.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }

        // 计算输入框相对于 visual viewport 的位置
        const rect = input.getBoundingClientRect();
        const visualViewport = window.visualViewport;
        const keyboardHeight = window.innerHeight - visualViewport.height;

        // 如果输入框被键盘遮挡，则滚动
        if (rect.bottom > visualViewport.height) {
          const scrollOffset = rect.bottom - visualViewport.height + 20; // 20px padding
          window.scrollBy({
            top: scrollOffset,
            behavior: 'smooth',
          });
        }
      }, 300); // iOS 键盘动画大约 300ms
    };

    input.addEventListener('focus', handleFocus);

    return () => {
      input.removeEventListener('focus', handleFocus);
    };
  }, [inputRef]);
}3
